import { tick } from 'svelte';
import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

export type ReorderStepVariant = 'vertical' | 'horizontal' | 'previousNext';

export type ReorderStepDetail = {
	index: number;
	delta: number;
	cardEl: HTMLElement;
};

/**
 * Phone-only chrome for step reorder (up/down or previous/next).
 */
export function isMobileReorderChrome(windowWidth: number): boolean {
	return windowWidth <= SystemScreenBreakpoints.MOBILE_MAX;
}

/**
 * Move one list item by `delta` slots. Returns a new array, or null when the move is out of bounds.
 */
export function moveItemInList<T>(items: T[], index: number, delta: number): T[] | null {
	const nextIndex = index + delta;
	if (index < 0 || nextIndex < 0 || nextIndex >= items.length) return null;

	const next = [...items];
	const [item] = next.splice(index, 1);
	next.splice(nextIndex, 0, item);
	return next;
}

function getScrollableParent(el: HTMLElement | null): HTMLElement | Window {
	let node = el?.parentElement ?? null;

	while (node) {
		const { overflowY } = getComputedStyle(node);
		const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
		if (canScroll && node.scrollHeight > node.clientHeight) {
			return node;
		}
		node = node.parentElement;
	}

	return window;
}

/**
 * Scroll the list so a moved card stays under the finger. No-op when already at the scroll edge.
 */
export function scrollListToKeepFingerAnchor(cardEl: HTMLElement | null, deltaY: number): void {
	if (!cardEl?.isConnected || !deltaY) return;

	const parent = getScrollableParent(cardEl);

	if (parent === window) {
		const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
		const next = Math.max(0, Math.min(maxScroll, window.scrollY + deltaY));
		if (next === window.scrollY) return;
		window.scrollTo({ top: next, behavior: 'auto' });
		return;
	}

	const scroller = parent as HTMLElement;
	const maxScroll = scroller.scrollHeight - scroller.clientHeight;
	const next = Math.max(0, Math.min(maxScroll, scroller.scrollTop + deltaY));
	if (next === scroller.scrollTop) return;
	scroller.scrollTo({ top: next, behavior: 'auto' });
}

const STEP_REORDER_DURATION_MS = 280;
const STEP_REORDER_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

type StepReorderSnapshot = {
	firstRects: Map<Element, DOMRect>;
	topBefore: number;
};

function prefersReducedMotion(): boolean {
	return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getReorderListRoot(cardEl: HTMLElement): HTMLElement | null {
	return cardEl.parentElement?.parentElement ?? null;
}

function getReorderItemEls(listRoot: HTMLElement | null): HTMLElement[] {
	if (!listRoot) return [];

	const motionEls = listRoot.querySelectorAll<HTMLElement>(':scope > * > .reorder-wrap');
	if (motionEls.length > 0) return Array.from(motionEls);

	return Array.from(listRoot.children).filter((el): el is HTMLElement => el instanceof HTMLElement);
}

function settleReorderMotion(listRoot: HTMLElement | null): void {
	for (const el of getReorderItemEls(listRoot)) {
		for (const animation of el.getAnimations()) {
			try {
				animation.finish();
			} catch {
				animation.cancel();
			}
		}
	}
}

/**
 * Measure list cells before a step reorder. Finishes any in-flight swap animation first.
 */
function captureStepReorderSnapshot(cardEl: HTMLElement): StepReorderSnapshot {
	const listRoot = getReorderListRoot(cardEl);
	settleReorderMotion(listRoot);

	const firstRects = new Map<Element, DOMRect>();
	for (const el of getReorderItemEls(listRoot)) {
		firstRects.set(el, el.getBoundingClientRect());
	}

	return { firstRects, topBefore: cardEl.getBoundingClientRect().top };
}

/**
 * Keep the tapped card under the finger, then FLIP the displaced neighbors into place.
 */
function playStepReorderMotion(cardEl: HTMLElement, snapshot: StepReorderSnapshot): void {
	scrollListToKeepFingerAnchor(cardEl, cardEl.getBoundingClientRect().top - snapshot.topBefore);

	if (prefersReducedMotion()) return;

	for (const [el, first] of snapshot.firstRects) {
		if (!(el instanceof HTMLElement) || !el.isConnected) continue;

		const last = el.getBoundingClientRect();
		const dx = first.left - last.left;
		const dy = first.top - last.top;
		if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;

		el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }], {
			duration: STEP_REORDER_DURATION_MS,
			easing: STEP_REORDER_EASING,
			fill: 'backwards',
		});
	}
}

/**
 * Move an item, keep the controls under the finger, and animate the rest of the list.
 */
export async function commitStepReorder<T>({
	items,
	index,
	delta,
	cardEl,
	assign,
}: {
	items: T[];
	index: number;
	delta: number;
	cardEl: HTMLElement;
	assign: (next: T[]) => void;
}): Promise<T[] | null> {
	const next = moveItemInList(items, index, delta);
	if (!next) return null;

	const snapshot = captureStepReorderSnapshot(cardEl);
	assign(next);
	await tick();
	playStepReorderMotion(cardEl, snapshot);
	return next;
}
